import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Star, Eye, EyeOff } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    author_name: '',
    rating: 5,
    comment: '',
    is_active: true
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews?active_only=false`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (review = null) => {
    if (review) {
      setEditingReview(review);
      setFormData({
        author_name: review.author_name,
        rating: review.rating,
        comment: review.comment,
        is_active: review.is_active
      });
    } else {
      setEditingReview(null);
      setFormData({
        author_name: '',
        rating: 5,
        comment: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReview) {
        await axios.put(`${API}/reviews/${editingReview.id}`, formData);
        toast.success('Avis mis à jour');
      } else {
        await axios.post(`${API}/reviews`, formData);
        toast.success('Avis créé');
      }
      setDialogOpen(false);
      fetchReviews();
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) return;
    
    try {
      await axios.delete(`${API}/reviews/${id}`);
      toast.success('Avis supprimé');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (review) => {
    try {
      await axios.put(`${API}/reviews/${review.id}`, { is_active: !review.is_active });
      fetchReviews();
      toast.success(review.is_active ? 'Avis masqué' : 'Avis affiché');
    } catch (error) {
      console.error('Error toggling review:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-stone-200 text-stone-200'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <AdminLayout title="Gestion des Avis Clients">
      <div className="space-y-6" data-testid="admin-reviews">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-stone-500">
            {reviews.length} avis client{reviews.length > 1 ? 's' : ''}
          </p>
          <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-review-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un avis
          </Button>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid gap-4">
            {reviews.map((review) => (
              <div 
                key={review.id} 
                className={`bg-white rounded-2xl border border-stone-200 p-6 ${!review.is_active ? 'opacity-50' : ''}`}
                data-testid={`review-item-${review.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold">
                          {review.author_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-stone-900">{review.author_name}</h3>
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    <p className="text-stone-600 mt-3">"{review.comment}"</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActive(review)}
                      className={`p-2 rounded-lg transition-colors ${
                        review.is_active 
                          ? 'text-emerald-600 hover:bg-emerald-50' 
                          : 'text-stone-400 hover:bg-stone-100'
                      }`}
                      title={review.is_active ? 'Masquer' : 'Afficher'}
                      data-testid={`toggle-review-${review.id}`}
                    >
                      {review.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => openDialog(review)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      data-testid={`edit-review-${review.id}`}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(review.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {reviews.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center text-stone-500">
                Aucun avis. Cliquez sur "Ajouter un avis" pour commencer.
              </div>
            )}
          </div>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingReview ? 'Modifier l\'avis' : 'Ajouter un avis'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="author_name">Nom du client</Label>
                <Input
                  id="author_name"
                  value={formData.author_name}
                  onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                  required
                  placeholder="Marie L."
                  data-testid="review-author-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rating">Note</Label>
                <Select
                  value={formData.rating.toString()}
                  onValueChange={(value) => setFormData({ ...formData, rating: parseInt(value) })}
                >
                  <SelectTrigger data-testid="review-rating-select">
                    <SelectValue placeholder="Sélectionner une note" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <SelectItem key={rating} value={rating.toString()}>
                        <div className="flex items-center gap-2">
                          {renderStars(rating)}
                          <span className="ml-2">{rating}/5</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="comment">Commentaire</Label>
                <Textarea
                  id="comment"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  required
                  rows={4}
                  placeholder="Super service, je recommande !"
                  data-testid="review-comment-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="review-active-switch"
                />
                <Label htmlFor="is_active">Afficher sur le site</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-review-btn">
                  {editingReview ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
